import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccessModal, setIsSuccessModal] = useState(false)

    const [formData, setFormData] = useState({
        password: '',
        password_confirm: ''
    });

    const [errors, setErrors] = useState({
        password: '',
        password_confirm: ''
    });

    const [touched, setTouched] = useState({
        password: false,
        password_confirm: false
    });

    const validatePassword = (password) => {
        const hasNumber = /\d/.test(password);
        const hasLetter = /[a-zA-Z]/.test(password);
        const hasSpecial = /[^A-Za-z\d]/.test(password);
        const isValidLength = password.length >= 8 && password.length <= 20;
        return hasNumber && hasLetter && hasSpecial && isValidLength;
    };

    const fetchPasswordReset = async (token) => {
        if (!token) {
            console.error("Token is missing");
            return;
        }

        try {
            setIsLoading(true);
            await api.get(`users/password-reset/?token=${token}`);
            setIsTokenValid(true);
        } catch (error) {
            console.error("Error validating token:", error);
            setIsTokenValid(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPasswordReset(token);
    }, [token]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (touched[name]) {
            validateField(name, value);
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched(prev => ({
            ...prev,
            [name]: true
        }));
        validateField(name, formData[name]);
    };

    const validateField = (name, value) => {
        if (name === 'password') {
            if (!value) return;
            if (!validatePassword(value)) {
                setErrors(prev => ({
                    ...prev,
                    password: '영문, 숫자, 특수문자를 조합한 8~20자로 입력하세요.'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    password: ''
                }));
            }
        }

        if (name === 'password_confirm') {
            if (!value) return;
            if (value !== formData.password) {
                setErrors(prev => ({
                    ...prev,
                    password_confirm: '비밀번호가 일치하지 않습니다'
                }));
            } else {
                setErrors(prev => ({
                    ...prev,
                    password_confirm: ''
                }));
            }
        }
    };

    const isFormValid = () => {
        return (
            validatePassword(formData.password) &&
            formData.password === formData.password_confirm &&
            formData.password_confirm !== ''
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid()) return;

        setIsLoading(true);
        try {
            const data = {
                token: token,
                password: formData.password,
                password_confirm: formData.password_confirm
            };

            const response = await api.put('users/password-reset/', data);
            setIsSuccessModal(true);
        } catch (error) {
            alert(error.response?.data?.message || "비밀번호 변경에 실패했습니다");
        } finally {
            setIsLoading(false);
        }
    };

    if (!isTokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-red-500">
                    유효하지 않은 링크입니다.
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="w-full max-w-md px-6 py-8">
                <h1 className="text-2xl font-bold mb-8 text-center">비밀번호 재설정</h1>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="새 비밀번호"
                        />
                        {touched.password && errors.password &&
                            <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                        }
                        <p className="mt-1 text-xs text-gray-500">
                            * 8~20자의 영문, 숫자, 특수문자를 포함해야 합니다
                        </p>
                    </div>

                    <div>
                        <input
                            type="password"
                            name="password_confirm"
                            value={formData.password_confirm}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="새 비밀번호 확인"
                        />
                        {touched.password_confirm && errors.password_confirm &&
                            <p className="mt-1 text-sm text-red-500">{errors.password_confirm}</p>
                        }
                    </div>

                    <button
                        type="submit"
                        className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        disabled={!isFormValid() || isLoading}
                    >
                        {isLoading ? "처리중..." : "비밀번호 변경"}
                    </button>
                </form>
            </div>

            {/* 성공 모달 */}
            {isSuccessModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-lg w-full max-w-sm p-6">
                        <p className="text-center mb-6">비밀번호가 변경되었습니다.</p>
                        <button
                            onClick={() => {
                                setIsSuccessModal(false);
                                navigate('/login');  // 로그인 페이지로 이동
                            }}
                            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                        >
                            확인
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResetPassword;